// Static lookup tables, formatters and the accordion/tab definitions shared by
// the Risk Areas page and its components. Nothing here touches React, Redux or
// the network — see ./services for anything that makes a request.
import {
  DirectionFilter,
  OutcomeFilter,
  RiskAreaFilters,
  StatusFilter,
} from "./types";
import { ViewMoreVariant } from "./services/risk-area-info-parser";

/** Rows per page for one accordion section. Also the API's own default. */
export const DEFAULT_PAGE_SIZE = 25;

/** Page size for the id-only walks behind "Bulk Update N results". 100 is the API's documented cap. */
export const BULK_ID_PAGE_SIZE = 100;

export const WEIGHT_TO_BUCKETS: Record<string, string[]> = {
  critical: ["critical"],
  high:     ["high"],
  moderate: ["moderate"],
  low:      ["low"],
};

/**
 * Every status the page asks for when it wants "everything". Listed explicitly:
 * the endpoints hide soft-deleted rows until "Deleted" is named, and naming it
 * alone returns *only* deleted rows.
 */
export const ALL_STATUSES: StatusFilter[] = [
  "New",
  "In-Progress",
  "Deferred",
  "Completed",
  "Deleted",
];

/**
 * Status tab key → the statuses its list and header calls ask for. Outcome is
 * not a tab — it's the toolbar's own dropdown, reaching the API through
 * `filters.outcome` independently.
 */
export const STATUS_TAB_TO_PARAMS: Record<string, { status?: StatusFilter[] }> = {
  new: { status: ["New"] },
  inprogress: { status: ["In-Progress"] },
  completed: { status: ["Completed"] },
  // Soft-deleted risks live in the Deferred tab rather than a tab of their own.
  deferred: { status: ["Deferred", "Deleted"] },
  // Names every status so the deleted rows are reachable — see ALL_STATUSES.
  all: { status: ALL_STATUSES },
};

/**
 * The buckets the tab badges label, and the `groupValue`s each sums from a
 * groupBy=Status response. Deferred sums two, which puts deleted risks in that
 * badge. `key` is also the `statusFilterCounts` map key ("<key>-<view>"), so
 * this table and the tab lists below must agree.
 */
export const STATUS_COUNT_BUCKETS: { key: string; groupValues: string[] }[] = [
  { key: "New", groupValues: ["New"] },
  { key: "In-Progress", groupValues: ["In-Progress"] },
  { key: "Completed", groupValues: ["Completed"] },
  { key: "Deferred", groupValues: ["Deferred", "Deleted"] },
];

/** The catch-all badge key — the sum of every STATUS_COUNT_BUCKETS group, not one the API returns. */
export const ALL_COUNT_KEY = "All";

export type RiskView = "billing" | "entity";

export const RISK_VIEWS: RiskView[] = ["billing", "entity"];

/**
 * How many risk area ids go on one /Api/RiskArea/BulkEditRiskArea call. The
 * endpoint interpolates the ids into a SQL `IN (...)` clause, so the unbounded
 * selection is split at this size rather than sent as one statement.
 */
export const BULK_EDIT_BATCH_SIZE = 500;

/**
 * How long a row's Weight keystroke waits before it is saved. Weight is typed,
 * not picked, so saving per keystroke costs a write, a grid invalidation and a
 * toast per digit.
 */
export const WEIGHT_SAVE_DEBOUNCE_MS = 500;

/** How long the Provider / Coder Group filter typeaheads wait before searching. */
export const GROUP_SEARCH_DEBOUNCE_MS = 500;

// The entity tab is the New Provider / New Coder sections; everything else
// (E&M, Metric, …) belongs to billing.
const isEntityType = (riskAreaType: string): boolean =>
  /provider|coder/i.test(riskAreaType);

/**
 * The risk area types a tab can contain, used to narrow the bulk update
 * dropdowns when "all results" was used and nothing is on screen to read types
 * off.
 *
 * Pass `allTypes` from the server's own vocabulary (the status list's
 * `riskAreaType`, e.g. 'E&M') — never a hardcoded literal. The combined-types
 * endpoints match on an exact `RiskAreaType IN (...)`, so one wrong string
 * silently empties the dropdown.
 */
export const viewRiskAreaTypes = (
  view: RiskView,
  allTypes: (string | null | undefined)[]
): string[] => {
  const seen = new Set<string>();
  allTypes.forEach((type) => {
    const value = (type ?? "").trim();
    if (value && isEntityType(value) === (view === "entity")) seen.add(value);
  });
  return Array.from(seen).sort();
};

/** A zeroed count map for every bucket on both views, so a badge renders "(0)" before counts land. */
export const emptyStatusCounts = (): Record<string, number> => {
  const counts: Record<string, number> = {};
  RISK_VIEWS.forEach((v) => {
    STATUS_COUNT_BUCKETS.forEach((b) => { counts[`${b.key}-${v}`] = 0; });
    counts[`${ALL_COUNT_KEY}-${v}`] = 0;
  });
  return counts;
};

// Status tab key → the STATUS_COUNT_BUCKETS key whose count that tab renders.
// Its own table, not read off STATUS_TAB_TO_PARAMS: the All tab's count comes
// from the bucket keyed "All", not from summing the other four.
const STATUS_TAB_TO_COUNT_KEY: Record<string, string> = {
  new: "New",
  inprogress: "In-Progress",
  completed: "Completed",
  deferred: "Deferred",
  all: "All",
};

/** The `statusFilterCounts` key for one tab/view. The only place that knows the "<bucket key>-<view>" format. */
export const statusCountKey = (statusFilter: string, view: RiskView): string =>
  `${STATUS_TAB_TO_COUNT_KEY[statusFilter] ?? statusFilter}-${view}`;

export const DIRECTION_TO_PARAM: Record<string, DirectionFilter> = {
  Audit: "Audit",
  Revenue: "Revenue",
};

/** Lookback label ("1 Quarter") → quarter integer, clamped to 1–4. */
export const lookbackToQuarter = (lookback: string): number => {
  const n = parseInt(lookback, 10);
  return isNaN(n) ? 1 : Math.min(4, Math.max(1, n));
};

/**
 * The risk type text used to pick a View More layout. Every field is joined
 * because any one can be blank — relying on category alone collapses the popup
 * to "metric" whenever it's empty.
 */
export const viewMoreRowLabel = (card: any): string =>
  [
    card?.riskAreaType,
    card?.riskAreaCategory,
    card?.riskAreaTypeLabel,
    card?.typeBadge,
    card?.emCategory ? "e&m" : null,
  ]
    .filter(Boolean)
    .join(" ");

// The status tabs, in workflow order with the catch-all last. Kept as two lists
// so the views can diverge again. "Deferred" covers soft-deleted risks too — the
// label reads "Deferred/Deleted" but the key stays "deferred".
export const BILLING_STATUS_TABS = [
  { key: "new", label: "New" },
  { key: "inprogress", label: "In Progress" },
  { key: "completed", label: "Completed" },
  { key: "deferred", label: "Deferred/Deleted" },
  { key: "all", label: "All" },
];

export const ENTITY_STATUS_TABS = [
  { key: "new", label: "New" },
  { key: "inprogress", label: "In Progress" },
  { key: "completed", label: "Completed" },
  { key: "deferred", label: "Deferred/Deleted" },
  { key: "all", label: "All" },
];

// ── Accordion sections ───────────────────────────────────────────────────────
// Disjoint by outcome: undispositioned work sits in weight/entity sections;
// marking a risk Satisfactory or Unsatisfactory moves it to that outcome's
// section. Full walkthrough: docs/pages/risk-areas.md.

/** The outcomes that keep a risk in the weight / entity sections. */
export const UNDISPOSITIONED_OUTCOMES: OutcomeFilter[] = ["Unreviewed", "Not Applicable"];

export type SectionGrouping = "weight" | "outcome" | "entity";

/** Which list endpoint backs an entity-tab section. Billing sections leave this unset — they all read /risk-areas/billing. */
export type EntitySource = "provider" | "coder";

/** One accordion section: its heading, its count source and its row scope. */
export interface SectionDef {
  id: string;
  title: string;
  badge?: string;
  badgeCls?: string;
  // Trailing text on the count label, e.g. "12 Risks" / "3 risks closed this cycle".
  noun: string;
  note?: string;
  grouping: SectionGrouping;
  // The header `groupValue` this section's count is read from. Case-insensitive:
  // weight buckets arrive lowercase, everything else title-case.
  groupValue: string;
  // The `outcome` values this section's list call is scoped to. Empty means no
  // outcome constraint of its own.
  outcomes: OutcomeFilter[];
  // Entity tab only: which list endpoints back this section's rows.
  sources?: EntitySource[];
}

export const BILLING_SECTIONS: SectionDef[] = [
  {
    id: "critical",
    title: "Critical Risk Priority",
    badge: "WEIGHTS 4+",
    badgeCls: "badge-critical",
    noun: "Risks",
    grouping: "weight",
    groupValue: "critical",
    outcomes: UNDISPOSITIONED_OUTCOMES,
  },
  {
    id: "high",
    title: "High Risk Priority",
    badge: "WEIGHT 3",
    badgeCls: "badge-high",
    noun: "Active Risks",
    grouping: "weight",
    groupValue: "high",
    outcomes: UNDISPOSITIONED_OUTCOMES,
  },
  {
    id: "moderate",
    title: "Moderate Risk",
    badge: "WEIGHT 2",
    badgeCls: "badge-moderate",
    noun: "risks",
    note: "Review after critical and high",
    grouping: "weight",
    groupValue: "moderate",
    outcomes: UNDISPOSITIONED_OUTCOMES,
  },
  {
    id: "low",
    title: "Low Risk Priority",
    badge: "WEIGHT 0-1",
    badgeCls: "badge-low",
    noun: "risks",
    note: "Lowest priority — review after moderate",
    grouping: "weight",
    groupValue: "low",
    outcomes: UNDISPOSITIONED_OUTCOMES,
  },
  {
    id: "satisfactory",
    title: "Satisfactory",
    noun: "risks closed this cycle",
    grouping: "outcome",
    groupValue: "Satisfactory",
    outcomes: ["Satisfactory"],
  },
  {
    id: "unsatisfactory",
    title: "Unsatisfactory",
    noun: "risks · corrective actions in place",
    grouping: "outcome",
    groupValue: "Unsatisfactory",
    outcomes: ["Unsatisfactory"],
  },
];

// New Provider & Coder tab, partitioned like Billing. Outcome sections read
// BOTH list endpoints and merge the result, so a Satisfactory provider and
// coder share one section; entity sections read one endpoint each.
//
// The "-entity" id suffix matters: section ids are one flat space across both
// tabs, so these must not collide with Billing's "satisfactory".
export const ENTITY_SECTIONS: SectionDef[] = [
  {
    id: "new-providers",
    title: "New Providers",
    noun: "providers",
    grouping: "entity",
    groupValue: "Provider",
    outcomes: UNDISPOSITIONED_OUTCOMES,
    sources: ["provider"],
  },
  {
    id: "new-coders",
    title: "New Coders",
    noun: "coders",
    grouping: "entity",
    groupValue: "Coder",
    outcomes: UNDISPOSITIONED_OUTCOMES,
    sources: ["coder"],
  },
  {
    id: "satisfactory-entity",
    title: "Satisfactory",
    noun: "risks closed this cycle",
    grouping: "outcome",
    groupValue: "Satisfactory",
    outcomes: ["Satisfactory"],
    sources: ["provider", "coder"],
  },
  {
    id: "unsatisfactory-entity",
    title: "Unsatisfactory",
    noun: "risks · corrective actions in place",
    grouping: "outcome",
    groupValue: "Unsatisfactory",
    outcomes: ["Unsatisfactory"],
    sources: ["provider", "coder"],
  },
];

// Narrows a toolbar multi-select to the values a section allows. No toolbar
// selection means "All". An INTERSECTION, not a merge — concatenating instead
// would ask for the union under IN semantics and pull dispositioned rows into
// a weight bucket.
const narrow = <T,>(selected: T[] | undefined, allowed: T[]): T[] => {
  if (!allowed.length) return selected ?? [];
  if (!selected || !selected.length) return allowed;
  return allowed.filter((value) => selected.includes(value));
};

/**
 * The outcome scope for the undispositioned sections' header call. Empty means
 * the toolbar picked a disposition, so skip that header request entirely.
 */
export const undispositionedOutcomes = (
  selected: OutcomeFilter[] | undefined
): OutcomeFilter[] => narrow(selected, UNDISPOSITIONED_OUTCOMES);

/**
 * The list-call filters for one section: the toolbar's filters narrowed by its
 * grouping. `null` means the section can't contain anything under the current
 * filters — skip the request entirely.
 */
export const sectionListFilters = (
  def: SectionDef,
  filters: RiskAreaFilters
): RiskAreaFilters | null => {
  const outcome = narrow(filters.outcome, def.outcomes);
  if (def.outcomes.length && !outcome.length) return null;

  const scoped: RiskAreaFilters = {
    ...filters,
    outcome: outcome.length ? outcome : undefined,
  };

  if (def.grouping !== "weight") return scoped;

  const weightBuckets = narrow(filters.weightBuckets, [def.groupValue]);
  if (!weightBuckets.length) return null;
  return { ...scoped, weightBuckets };
};

/** Heading per View More layout. The popup names the *kind* of risk area; identity is the first line of the body. */
export const VIEW_MORE_HEADINGS: Record<ViewMoreVariant, string> = {
  metric: "Metric Risk Area",
  emTrend: "E&M Bell Curve Trend Risk Area",
  emUtilization: "E&M Service Utilization Risk Area",
  emVariance: "E&M Variance Risk Area",
  newProvider: "New Provider Risk Area",
  newCoder: "New Coder Risk Area",
};
